import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Address } from '@ridgewill/brand/react';
import { Field, Note, Problem, button, input } from '../components/form.js';
import { Modal } from '../components/Modal.js';
import { SecurityGate, type Credential } from '../components/SecurityGate.js';
import {
  addressesAgree,
  cancelChange,
  requestChange,
  willNeedCurrentAddressApproval,
  type PendingChangeState,
} from '../lib/email-change.js';
import { providerNames, type SignInMethods } from '../lib/sign-in-methods.js';
import { useEmailVerified } from '../lib/email-verification.js';
import { UnverifiedEmailNotice } from '../components/EmailVerification.js';

/**
 * The address this account is reached at, and moving it to another one.
 *
 * ── FIRST ON THE PAGE, ABOVE "HOW YOU SIGN IN" ───────────────────────
 *
 * Because on this service the email address is the de facto master credential,
 * not a contact detail: every provider sign-in resolves through it
 * (RidgeWill/auth#324), so whoever controls the mailbox controls the account.
 * A screen about how you get in that put the address below the passkeys would
 * be ordering by how the storage looks rather than by what matters.
 *
 * It is also where an unwanted change has to be visible. account#54 came from
 * one question — *if somebody changed my address and I still sign in with the
 * old one, can I cancel it?* — and until RidgeWill/auth#327 the answer was no,
 * because a pending change was a signed link in an inbox and nothing else. Now
 * it is recorded, so it can be shown and taken back, and the person who gets
 * the notice and signs in finds it named at the top of this page instead of one
 * that looks entirely normal.
 *
 * ── ASKING DOES NOT CHANGE ANYTHING, AND THAT IS THE HARD PART ───────
 *
 * A 200 from `/change-email` means a link was sent. Every piece of copy below
 * is written around that, because a form that says "Saved" and leaves the old
 * address on screen reads as a bug.
 */
export function EmailAddress({
  email,
  methods,
  twoFactorEnabled,
  passkeyCount,
  pending,
  onChanged,
  onSessionChanged = () => {},
}: {
  /** The CURRENT address. The one being moved away from. */
  email: string;
  methods: SignInMethods | null | undefined;
  twoFactorEnabled: boolean;
  /** Negative when unknown. See `loadPasskeyCount`. */
  passkeyCount: number;
  /** `undefined` while the lookup is outstanding. */
  pending: PendingChangeState | undefined;
  /** Re-reads the pending change. */
  onChanged: () => void;
  /** Re-reads the SESSION, which is where the address itself comes from. The
   *  one-time correction changes it (account#112). */
  onSessionChanged?: () => void;
}) {
  const verified = useEmailVerified();

  return (
    <>
      <Field label="Current email address">
        <p style={current}>
          <Address>{email}</Address>
        </p>
      </Field>

      {/*
        UNVERIFIED: the resend control in place of the change form
        (account#112). `/change-email` refuses an unverified account with
        EMAIL_NOT_VERIFIED, so offering it would be a form that can only fail;
        a typo is fixed by the one-time correction inside this notice instead.
        Keyodo's app links here as `/security#email`.
      */}
      {!verified ? (
        <>
          <UnverifiedEmailNotice onChanged={onSessionChanged} />
          <div style={{ marginTop: '1.5rem' }}>
            <h3 style={formHeading}>Change your email address</h3>
            <Note>Verify your email to change it.</Note>
          </div>
        </>
      ) : null}

      <Pending state={pending} email={email} onChanged={onChanged} />

      {verified ? <ChangeForm
        email={email}
        methods={methods}
        twoFactorEnabled={twoFactorEnabled}
        passkeyCount={passkeyCount}
        pending={pending}
        onChanged={onChanged}
      /> : null}
    </>
  );
}

/**
 * The pending change, when there is one.
 *
 * ── FOUR STATES, AND "NOTHING PENDING" IS ONLY ONE OF THEM ───────────
 *
 * Outstanding and failed both render, quietly, rather than falling through to
 * the same silence as `none`. Drawing nothing for a failed lookup states that
 * no change is pending, which is a claim about somebody's account made from no
 * evidence, and it is the reassuring direction to be wrong in.
 */
function Pending({
  state,
  email,
  onChanged,
}: {
  state: PendingChangeState | undefined;
  email: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state === undefined) return <p style={{ margin: 0, color: C.muted }}>Checking&hellip;</p>;

  if (state.status === 'unknown') {
    return (
      <Note tone="bad">
        We could not check whether a change to your email address is outstanding. Nothing has
        changed, so try again in a moment.
      </Note>
    );
  }

  if (state.status === 'none') return null;

  async function cancel() {
    setBusy(true);
    setError(null);
    const message = await cancelChange();
    setBusy(false);
    if (message) {
      setError(message);
      return;
    }
    onChanged();
  }

  return (
    /**
     * `role="status"` rather than `alert`. This is a standing condition
     * somebody has arrived at, not something that just happened in front of
     * them, and an assertive announcement on every page load is the shape of
     * warning people learn to ignore.
     */
    <div role="status" style={pendingCard}>
      <h3 style={pendingHeading}>A change to your email address is waiting</h3>

      <p style={pendingLine}>
        Somebody asked to move this account to <strong><Address>{state.newEmail}</Address></strong>
        {state.requestedAt ? ` on ${state.requestedAt.toLocaleString()}` : null}.
      </p>

      <p style={pendingLine}>
        {state.awaitingApproval ? (
          <>
            It needs approving from your current address, <strong><Address>{email}</Address></strong>,
            so check that inbox. Until you do, your address stays as it is.
          </>
        ) : (
          <>
            It takes effect when the link sent to that address is opened. Until then, your address
            stays as it is.
          </>
        )}
      </p>

      {/*
        The sentence the whole feature exists for. Cancelling makes the
        outstanding link inert, which is what nothing did before auth#327:
        a signed token in somebody's inbox depended on no state at all.
      */}
      <p style={pendingLine}>
        If you did not ask for this, cancel it and then change your password. That signs out every
        other device as well.
      </p>

      {error ? <Problem>{error}</Problem> : null}

      <button type="button" onClick={() => void cancel()} disabled={busy} style={button}>
        {busy ? 'Cancelling…' : 'Cancel this change'}
      </button>
    </div>
  );
}

/**
 * Ask to move the account to a different address.
 *
 * ── THE PASSWORD FIELD IS DRAWN FROM `hasPassword`, NOT ALWAYS ───────
 *
 * The auth service refuses without the current password when there is one and
 * exempts an account that has none, because an account created through Google
 * has no credential row and demanding a password there would make the address
 * permanently unchangeable for exactly the people RidgeWill/auth#324 is about.
 * Asking for a password nobody has would be a box that can only be wrong.
 *
 * ── AND THE FORM WARNS ABOUT THE GATE BEFORE IT IS HIT ───────────────
 *
 * A password-only account has to approve the move from the address it is
 * leaving. That is knowable here, and somebody who is not told will watch the
 * new inbox for a change that cannot complete until they act in the old one.
 * The warning is advisory: the server decides, and this errs toward silence
 * when the passkey count is unknown.
 */
/**
 * Ask to move the account to a different address, behind the security gate.
 *
 * ── THE PASSWORD IS ASKED FOR FIRST, IN A MODAL (Jon, 2026-09-01) ────
 *
 * It used to sit beside the new-address field, so the whole form was visible
 * and fillable to anyone who walked up to an unlocked laptop, and the refusal
 * arrived only after they had typed everything. Now the dialog asks, and the
 * field appears once the answer is right.
 *
 * The password is still SENT with the request. `SecurityGate` says at length
 * why: `/reauthenticate` grants nothing, and `/change-email` checks the
 * credential itself on its own request. Dropping it here because the gate
 * passed would put the only real check in a browser.
 *
 * ── AND THE GATE WARNING STAYS ON STEP TWO ──────────────────────────
 *
 * A password-only account has to approve the move from the address it is
 * leaving, and that is worth knowing while deciding what to type — not on the
 * step where somebody is confirming who they are.
 */
function ChangeForm({
  email,
  methods,
  twoFactorEnabled,
  passkeyCount,
  pending,
  onChanged,
}: {
  email: string;
  methods: SignInMethods | null | undefined;
  twoFactorEnabled: boolean;
  passkeyCount: number;
  pending: PendingChangeState | undefined;
  onChanged: () => void;
}) {
  /**
   * `null` while the gate is unanswered, an object once it has passed.
   *
   * One value for both facts, so there is no way to be past the gate without a
   * credential or to hold one without having passed. Closing the dialog drops
   * it, which is the only place that password should ever live.
   */
  const [open, setOpen] = useState(false);
  const [credential, setCredential] = useState<Credential | null>(null);
  const [newEmail, setNewEmail] = useState('');
  /** Typed a second time. See `addressesAgree` for why this field exists at
   *  all: a mistyped address fails silently, in somebody else's inbox. */
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  /**
   * Nothing is drawn until the sign-in methods are in, for the reason the
   * Password card gives: a control that changes shape a moment after it
   * appears moves out from under the cursor.
   *
   * A FAILED lookup falls back to asking for the password. That is the safe
   * direction of the two — the server exempts an account with no credential
   * row whatever is sent, so an unnecessary prompt is a wasted keystroke,
   * while a missing one is a refusal the person cannot act on.
   */
  if (methods === undefined) return <p style={{ margin: 0, color: C.muted }}>Checking&hellip;</p>;

  const hasPassword = methods?.hasPassword !== false;
  const willBeGated = willNeedCurrentAddressApproval({
    hasPassword: methods?.hasPassword === true,
    twoFactorEnabled,
    passkeyCount,
    providerCount: methods?.providers.length ?? 0,
  });

  /**
   * The providers this change is about to disconnect (RidgeWill/auth#345).
   *
   * Named up front because it is the one consequence of a change that is not
   * reversible by changing back: a link can only be made while the provider's
   * address matches the account's, so after moving to a new address the old
   * provider account cannot be reconnected at all. Somebody who would not have
   * done it knowing that deserves to know it before the link is sent, not from
   * a refused sign-in a week later.
   */
  const linked = methods?.providers.map((provider) => provider.providerId) ?? [];

  const wanted = newEmail.trim();
  /**
   * Same address, refused here rather than by the server. Better Auth raises it
   * before any of our hooks, with no `code` and the string "Email is the same",
   * and the shortest route to a good message is not sending the request.
   *
   * Case-insensitively, because the server lower-cases before comparing and a
   * capitalised copy of your own address is not a different address.
   */
  const same = wanted.length > 0 && wanted.toLowerCase() === email.trim().toLowerCase();

  /**
   * The mismatch is shown only once the second field has something in it —
   * otherwise every address reads as wrong while it is still being typed, and
   * a form that is red from the first keystroke stops being read at all.
   */
  const confirmed = addressesAgree(newEmail, confirmEmail);
  const mismatch = confirmEmail.trim().length > 0 && !confirmed;
  const ready = wanted.length > 0 && !same && confirmed;

  function close() {
    setOpen(false);
    setCredential(null);
    setNewEmail('');
    setConfirmEmail('');
    setError(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const message = await requestChange(wanted, credential?.password);
    setBusy(false);
    if (message) {
      setError(message);
      return;
    }
    /**
     * The dialog closes and the confirmation lands in the card, beside the
     * pending block about to appear. Leaving it open with a success note inside
     * invites a second submit of a form whose field has just been cleared.
     */
    setSent(wanted);
    close();
    onChanged();
  }

  const replacing = pending?.status === 'pending';

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h3 style={formHeading}>Change your email address</h3>

      <Note>
        Your address does not change when you ask. A link is sent to the new address, and opening
        it is what moves the account.
      </Note>

      {sent ? (
        /**
         * TRUE WHETHER OR NOT ANYTHING WAS SENT, WHICH IS THE POINT.
         *
         * An address already in use answers 200 and sends nothing, deliberately,
         * so this form cannot be used to find out who has an account here. Copy
         * promising that a message is on its way to a named inbox would be a lie
         * in that case, and copy hedging only in that case would give the answer
         * away. "If that address can be used" is the same sentence for everybody
         * and false for nobody.
         */
        <Note tone="ok">
          Your email address has not changed yet. If <strong><Address>{sent}</Address></strong> can be
          used, a link is on its way there, and opening it is what completes the move.
        </Note>
      ) : null}

      <p style={{ marginTop: '1rem' }}>
        <button type="button" style={button} onClick={() => setOpen(true)}>
          Change email
        </button>
      </p>

      {/*
        RENDERED ALWAYS, with `open` as a prop. The dialog owns its own removal
        so it can fade OUT — `{open ? ... : null}` removes the element on the
        frame the flag flips and leaves nothing to animate. See components/Modal.
      */}
      <Modal open={open} title="Change your email address" onClose={close}>
          {credential === null ? (
            <SecurityGate
              action="change your email address"
              hasPassword={hasPassword}
              onVerified={setCredential}
              onCancel={close}
            />
          ) : (
            <form onSubmit={(e) => void submit(e)}>
              {replacing ? (
                <Note tone="bad">
                  Asking again replaces the change already waiting, rather than adding a second
                  one.
                </Note>
              ) : null}

              <Field id="new-email" label="New email address">
                <input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  autoComplete="email"
                  style={input}
                  /* The field this dialog exists to reveal, so it takes focus
                     rather than making somebody click into it. */
                  autoFocus
                />
                {same ? <Note tone="bad">That is already your email address.</Note> : null}
              </Field>

              {/*
                TYPED AGAIN, because a mistake here is invisible. `/change-email`
                answers 200 whether or not anything was sent, so a typo produces
                the same screen as a success and the only evidence is a link
                that never arrives.

                `autoComplete="off"` on this one alone: a browser offering to
                fill the address it just filled above would defeat the point of
                asking twice. The first field keeps `email`, because there the
                suggestion is a convenience rather than an echo.

                PASTE IS NOT BLOCKED, though "type it again" invites it. NCSC
                and OWASP both advise against blocking paste, and the arithmetic
                is against it here too: the mistake this catches is a TYPO, and
                a typo is not pasted twice identically unless somebody copied
                their own. Blocking it would punish the careful person pasting
                from a known-good source, and anyone using a password manager or
                assistive input, to inconvenience nobody.
              */}
              <Field id="confirm-email" label="Confirm the new email address">
                <input
                  id="confirm-email"
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  autoComplete="off"
                  style={input}
                />
                {mismatch ? <Note tone="bad">These addresses do not match.</Note> : null}
              </Field>

              {linked.length > 0 ? (
                <Note tone="bad">
                  Completing this will disconnect {providerNames(linked)}. Sign-in providers are
                  tied to the address they were connected with, so you will only be able to
                  reconnect one that uses your new address.
                </Note>
              ) : null}

              {willBeGated ? (
                /**
                 * Jon, on account#54: nudging somebody toward a passkey is the
                 * honest way out of the gate, and this is a good place to say
                 * so. The gate is OWASP's substitute for strong proof, so
                 * adding one genuinely removes the step rather than skipping a
                 * check.
                 */
                <Note>
                  This account has no passkey or authenticator app, so the change will also need
                  approving from your current address. Adding a passkey removes that step.
                </Note>
              ) : null}

              {error ? <Problem>{error}</Problem> : null}

              <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                <button type="submit" disabled={busy || !ready} style={button}>
                  {busy ? 'Sending…' : 'Send the link'}
                </button>
                <button type="button" onClick={close} style={button}>
                  Cancel
                </button>
              </div>
            </form>
          )}
      </Modal>
    </div>
  );
}

/** Looks like the value it is, not like a box somebody failed to type in. The
 *  same treatment the read-only username row gets on Profile. */
const current: React.CSSProperties = {
  margin: 0,
  padding: '.6rem 0',
  fontSize: '1rem',
};

/* The `address` style is gone: `Address` from @ridgewill/brand/react owns
 * where an address may break, so the two cannot disagree. */

/**
 * Bordered and tinted, unlike anything else on the page.
 *
 * The card it sits inside is already a container, so a plain paragraph here
 * would read as more of the same section rather than as a condition needing
 * attention. Amber rather than red: nothing has gone wrong yet, and a red panel
 * describes damage rather than a decision somebody still has time to make.
 *
 * Colour is not the only signal. The heading says what it is and the button
 * says what it does, so a reader who cannot distinguish the border loses
 * nothing.
 */
const pendingCard: React.CSSProperties = {
  marginTop: '1rem',
  padding: '1rem',
  /* The brand's warn trio rather than three literals picked against white
     (account#87). A `#fdf7e8` background is the one that fails hardest in
     dark: near-white, so the card inverts into the brightest thing on the
     page and the heading's `#7a5a00` goes unreadable on top of it. */
  border: `1px solid ${C.warnDot}`,
  borderRadius: 8,
  background: C.warnBg,
};

const pendingHeading: React.CSSProperties = {
  margin: '0 0 .5rem',
  fontSize: '1rem',
  color: C.warnText,
};

const pendingLine: React.CSSProperties = {
  margin: '0 0 .6rem',
  lineHeight: 1.5,
  fontSize: '.95rem',
};

const formHeading: React.CSSProperties = {
  margin: '0 0 .5rem',
  fontSize: '1rem',
};
